import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Badge, Button } from '@fyos/components';
import { Plus } from 'lucide-react';
import BoardItemCard from './BoardItemCard';
import type { Item, Category } from '@/lib/itemApi';

export interface ColumnDef {
  id: string;
  name: string;
  description?: string;
  color: string;
  hexColor?: string;
  autoKill: number | null;
}

export interface BoardColumnProps {
  column: ColumnDef;
  items: Item[];
  categories: Category[];
  onAddItem: (columnId: string) => void;
  onItemClick: (item: Item) => void;
  selectedItems?: Set<string>;
  onSelectItem?: (itemId: string, selected: boolean) => void;
  onVote?: (itemId: string, direction: 'up' | 'down') => void;
}

const COLUMN_BG: Record<string, string> = {
  now: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800',
  next: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800',
  later: 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800',
  shipped: 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800',
  killed: 'bg-gray-50 border-gray-200 dark:bg-gray-800/50 dark:border-gray-700',
};

// Default descriptions for standard columns
const COLUMN_DESCRIPTION: Record<string, string> = {
  now: 'Currently being worked on',
  next: 'Coming up soon',
  later: 'On the horizon',
  shipped: 'Completed and released',
  killed: 'Not happening',
};

export default function BoardColumn({
  column,
  items,
  categories,
  onAddItem,
  onItemClick,
  selectedItems,
  onSelectItem,
  onVote,
}: BoardColumnProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: column.id,
    data: { column },
  });

  const getCategoryById = (categoryId?: string) =>
    categories.find(c => c.id === categoryId);

  // const isDragDisabled = column.id === 'shipped' || column.id === 'killed';
  const isDragDisabled = false;

  const hasKnownBg = !!COLUMN_BG[column.id];

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col rounded-md border transition-colors ${
        hasKnownBg ? COLUMN_BG[column.id] : 'border-gray-200 dark:border-gray-700'
      } ${
        isOver ? 'ring-2 ring-brand-500 ring-offset-2' : ''
      }`}
      style={!hasKnownBg && column.hexColor ? { backgroundColor: column.hexColor + '10', borderColor: column.hexColor + '40' } : undefined}
    >
      <div className="flex items-start justify-between px-3 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-[Petrona] text-2xl font-semibold text-gray-900 dark:text-white leading-tight tracking-[-1px]">
            {column.name}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {column.description || COLUMN_DESCRIPTION[column.id] || ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 ml-2">
          <Badge size="sm" variant="gray">
            {items.length}
          </Badge>
          {column.autoKill && (
            <Badge size="sm" variant="warning">
              {column.autoKill}d
            </Badge>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-2" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
          {items.map(item => (
            <BoardItemCard
              key={item.id}
              item={item}
              category={getCategoryById(item.category_id)}
              onClick={() => onItemClick(item)}
              isDragDisabled={isDragDisabled}
              isSelected={selectedItems?.has(item.id)}
              onSelect={onSelectItem}
              onVote={onVote}
            />
          ))}
        </SortableContext>
        {items.length === 0 && (
          <div className="py-6 text-center text-xs text-gray-400 dark:text-gray-500">
            No items
          </div>
        )}
      </div>

      <div className="px-2 pb-2">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-1 border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:text-gray-200"
          onClick={() => onAddItem(column.id)}
        >
          <Plus className="h-3.5 w-3.5" />
          Add item
        </Button>
      </div>
    </div>
  );
}
